import React, { Component } from "react";
import { withModulesManager, formatMessage } from "@openimis/fe-core";
import { injectIntl } from "react-intl";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  Typography,
  Box,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Card,
  CardContent,
  FormHelperText,
} from "@material-ui/core";
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as BackIcon,
} from "@material-ui/icons";
import { withStyles } from "@material-ui/core/styles";
import {
  fetchPolicyholderEnrollment,
  updatePolicyholderEnrollment,
  submitPolicyholderEnrollment,
} from "../../actions";
import {
  toBackendPolicyholderPayload,
  normalizePolicyholderData,
} from "../../utils/enrollmentData";

const styles = (theme) => ({
  page: {
    margin: theme.spacing(1),
  },
  header: {
    marginBottom: theme.spacing(3),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  backButton: {
    marginRight: theme.spacing(2),
  },
  title: {
    fontWeight: "bold",
    color: theme.palette.primary.main,
    flex: 1,
  },
  form: {
    marginTop: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  formCard: {
    marginBottom: theme.spacing(2),
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  saveButton: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
});

class PolicyHolderEnrollmentEdit extends Component {
  state = {
    enrollment: {
      policyHolderId: "",
      companyName: "",
      contactPerson: "",
      email: "",
      phone: "",
      status: "Active",
      enrollmentDate: "",
      validityFrom: "",
      validityTo: "",
      employeeCount: "",
      premiumAmount: "",
      industry: "",
      address: "",
      website: "",
      registrationNumber: "",
      policyNumber: "",
      coverageType: "",
      deductible: "",
    },
    errors: {},
    isLoading: false,
  };

  componentDidMount() {
    const uuid = this.props.match?.params?.enrollmentId;
    if (uuid && this.props.fetchPolicyholderEnrollment) {
      this.props.fetchPolicyholderEnrollment(this.props.modulesManager, uuid);
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.policyholderEnrollment &&
      this.props.policyholderEnrollment !== prevProps.policyholderEnrollment
    ) {
      const e = this.props.policyholderEnrollment;
      const d = normalizePolicyholderData(e.policyholderData);
      this.setState({
        enrollment: {
          uuid: e.uuid,
          policyHolderId: d.code || "",
          companyName: d.name || "",
          contactPerson: d.contactName || "",
          email: d.email || "",
          phone: d.phone || "",
          status: e.status || "DRAFT",
          enrollmentDate: (e.dateCreated || "").toString().slice(0, 10),
          validityFrom: "",
          validityTo: "",
          employeeCount: d.expectedMembers || "",
          premiumAmount: "",
          industry: d.type || "",
          address: typeof d.address === "object" ? "" : d.address || "",
          website: "",
          registrationNumber: d.registrationNumber || "",
          policyNumber: "",
          coverageType: "",
          deductible: "",
          notes: e.notes || "",
          code: d.code || "",
          tradeName: d.name || "",
          policyholderType: d.type || "",
          locationId: d.locationId || "",
        },
      });
    }
  }

  handleInputChange = (field) => (event) => {
    const value = event.target.value;
    this.setState({
      enrollment: {
        ...this.state.enrollment,
        [field]: value,
      },
      errors: {
        ...this.state.errors,
        [field]: "",
      },
    });
  };

  validateForm = () => {
    const { enrollment } = this.state;
    const errors = {};

    if (!enrollment.policyHolderId.trim()) {
      errors.policyHolderId = "Policy Holder ID is required";
    }
    if (!enrollment.companyName.trim()) {
      errors.companyName = "Company name is required";
    }
    if (!enrollment.contactPerson.trim()) {
      errors.contactPerson = "Contact person is required";
    }
    if (!enrollment.enrollmentDate) {
      errors.enrollmentDate = "Enrollment date is required";
    }
    if (
      enrollment.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(enrollment.email)
    ) {
      errors.email = "Please enter a valid email address";
    }

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  handleSave = () => {
    if (!this.validateForm()) return;
    const { enrollment } = this.state;
    const uuid = enrollment?.uuid || this.props.policyholderEnrollment?.uuid;
    if (!uuid) return;

    this.setState({ isLoading: true });
    const payload = {
      uuid,
      policyholderData: toBackendPolicyholderPayload(enrollment),
      notes: enrollment.notes,
    };
    Promise.resolve(
      this.props.updatePolicyholderEnrollment(
        this.props.modulesManager,
        payload,
        "Update Policyholder Enrollment"
      )
    )
      .then(() => {
        this.setState({ isLoading: false });
        this.handleBack();
      })
      .catch(() => this.setState({ isLoading: false }));
  };

  handleCancel = () => {
    this.handleBack();
  };

  handleBack = () => {
    const uuid =
      this.state.enrollment?.uuid || this.props.policyholderEnrollment?.uuid;
    if (this.props.history && uuid) {
      this.props.history.push(`/enrollment/policyholder/${uuid}/detail`);
    } else {
      window.history.back();
    }
  };

  render() {
    const { classes, intl } = this.props;
    const { enrollment, errors, isLoading } = this.state;

    return (
      <Box className={classes.page}>
        <Card style={{ margin: 16 }}>
          <CardContent style={{ padding: 24 }}>
            <Box className={classes.header}>
              <Box display="flex" alignItems="center" flex={1}>
                <IconButton
                  className={classes.backButton}
                  onClick={this.handleBack}
                  color="primary"
                >
                  <BackIcon />
                </IconButton>
                <Typography variant="h4" className={classes.title}>
                  {formatMessage(
                    intl,
                    "enrollment",
                    "policyHolderEnrollment.edit.title"
                  )}
                </Typography>
              </Box>
            </Box>

            <form className={classes.form}>
              <Grid container spacing={3}>
                {/* Company Information Section */}
                <Grid item xs={12} md={6}>
                  <Card className={classes.formCard}>
                    <CardContent>
                      <Typography variant="h6" className={classes.sectionTitle}>
                        {formatMessage(
                          intl,
                          "enrollment",
                          "policyHolderEnrollment.edit.companyInfo"
                        )}
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "policyHolderEnrollment.policyHolderId"
                            )}
                            value={enrollment.policyHolderId}
                            onChange={this.handleInputChange("policyHolderId")}
                            error={!!errors.policyHolderId}
                            helperText={errors.policyHolderId}
                            required
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "policyHolderEnrollment.company"
                            )}
                            value={enrollment.companyName}
                            onChange={this.handleInputChange("companyName")}
                            error={!!errors.companyName}
                            helperText={errors.companyName}
                            required
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <FormControl fullWidth>
                            <InputLabel>
                              {formatMessage(
                                intl,
                                "enrollment",
                                "policyHolderEnrollment.industry"
                              )}
                            </InputLabel>
                            <Select
                              value={enrollment.industry}
                              onChange={this.handleInputChange("industry")}
                            >
                              <MenuItem value="Technology">Technology</MenuItem>
                              <MenuItem value="Manufacturing">
                                Manufacturing
                              </MenuItem>
                              <MenuItem value="Healthcare">Healthcare</MenuItem>
                              <MenuItem value="Education">Education</MenuItem>
                              <MenuItem value="Services">Services</MenuItem>
                              <MenuItem value="Finance">Finance</MenuItem>
                              <MenuItem value="Other">Other</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Registration Number"
                            value={enrollment.registrationNumber}
                            onChange={this.handleInputChange(
                              "registrationNumber"
                            )}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Website"
                            value={enrollment.website}
                            onChange={this.handleInputChange("website")}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Address"
                            value={enrollment.address}
                            onChange={this.handleInputChange("address")}
                            multiline
                            minRows={3}
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Contact Information Section */}
                <Grid item xs={12} md={6}>
                  <Card className={classes.formCard}>
                    <CardContent>
                      <Typography variant="h6" className={classes.sectionTitle}>
                        {formatMessage(
                          intl,
                          "enrollment",
                          "policyHolderEnrollment.edit.contactInfo"
                        )}
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "policyHolderEnrollment.contact"
                            )}
                            value={enrollment.contactPerson}
                            onChange={this.handleInputChange("contactPerson")}
                            error={!!errors.contactPerson}
                            helperText={errors.contactPerson}
                            required
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Email"
                            value={enrollment.email}
                            onChange={this.handleInputChange("email")}
                            error={!!errors.email}
                            helperText={errors.email}
                            type="email"
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Phone"
                            value={enrollment.phone}
                            onChange={this.handleInputChange("phone")}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "policyHolderEnrollment.employees"
                            )}
                            value={enrollment.employeeCount}
                            onChange={this.handleInputChange("employeeCount")}
                            type="number"
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  <Card className={classes.formCard}>
                    <CardContent>
                      <Typography variant="h6" className={classes.sectionTitle}>
                        {formatMessage(
                          intl,
                          "enrollment",
                          "policyHolderEnrollment.edit.enrollmentInfo"
                        )}
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel>
                              {formatMessage(
                                intl,
                                "enrollment",
                                "policyHolderEnrollment.status"
                              )}
                            </InputLabel>
                            <Select
                              value={enrollment.status}
                              onChange={this.handleInputChange("status")}
                            >
                              <MenuItem value="Active">Active</MenuItem>
                              <MenuItem value="Inactive">Inactive</MenuItem>
                              <MenuItem value="Pending">Pending</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "policyHolderEnrollment.enrollmentDate"
                            )}
                            type="date"
                            value={enrollment.enrollmentDate}
                            onChange={this.handleInputChange("enrollmentDate")}
                            error={!!errors.enrollmentDate}
                            helperText={errors.enrollmentDate}
                            InputLabelProps={{ shrink: true }}
                            required
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "policyHolderEnrollment.edit.validityFrom"
                            )}
                            type="date"
                            value={enrollment.validityFrom}
                            onChange={this.handleInputChange("validityFrom")}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "policyHolderEnrollment.edit.validityTo"
                            )}
                            type="date"
                            value={enrollment.validityTo}
                            onChange={this.handleInputChange("validityTo")}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "policyHolderEnrollment.edit.policyNumber"
                            )}
                            value={enrollment.policyNumber}
                            onChange={this.handleInputChange("policyNumber")}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "policyHolderEnrollment.premium"
                            )}
                            value={enrollment.premiumAmount}
                            onChange={this.handleInputChange("premiumAmount")}
                            type="number"
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Deductible"
                            value={enrollment.deductible}
                            onChange={this.handleInputChange("deductible")}
                            type="number"
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Coverage Type"
                            value={enrollment.coverageType}
                            onChange={this.handleInputChange("coverageType")}
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Box className={classes.buttonContainer}>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={this.handleCancel}
                  disabled={isLoading}
                >
                  {formatMessage(
                    intl,
                    "enrollment",
                    "policyHolderEnrollment.edit.cancel"
                  )}
                </Button>
                <Button
                  variant="contained"
                  className={classes.saveButton}
                  startIcon={<SaveIcon />}
                  onClick={this.handleSave}
                  disabled={isLoading}
                >
                  {isLoading
                    ? formatMessage(
                        intl,
                        "enrollment",
                        "policyHolderEnrollment.edit.saving"
                      )
                    : formatMessage(
                        intl,
                        "enrollment",
                        "policyHolderEnrollment.edit.save"
                      )}
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Box>
    );
  }
}

const mapStateToProps = (state) => ({
  policyholderEnrollment: state.enrollment?.policyHolderEnrollmentDetail || null,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchPolicyholderEnrollment,
      updatePolicyholderEnrollment,
      submitPolicyholderEnrollment,
    },
    dispatch
  );

export default withModulesManager(
  withStyles(styles)(
    injectIntl(
      connect(mapStateToProps, mapDispatchToProps)(PolicyHolderEnrollmentEdit)
    )
  )
);
